// Min-version gating for engines, driven off the version each engine reports in
// its MQTT presence payload (`app` field — see PresencePublisher / MQTTSubscriber).
//
// Purpose: let the server activate features only for engines new enough to handle
// them safely (e.g. re-dispatching a message only to engines whose store-check
// dedup guard is alive). Shared FE+BE so the client and server agree on the compare.
//
// FAIL-SAFE: anything unparseable / missing / older is treated as NOT meeting the
// minimum, so a feature stays OFF for engines we can't positively confirm are new
// enough (pre-capability engines report the legacy default "1.0.0" → excluded).

/** Parse a dotted version ("5.8.61") into numeric segments; null if unparseable. */
export function parseVersion(v: unknown): number[] | null {
    if (typeof v !== 'string') return null;
    const trimmed = v.trim();
    if (!/^\d+(\.\d+)*$/.test(trimmed)) return null;
    return trimmed.split('.').map((n) => parseInt(n, 10));
}

/**
 * True iff `version` >= `min` (segment-wise numeric compare; missing trailing
 * segments treated as 0, so "5.8" >= "5.8.0"). Returns false for any
 * unparseable/missing `version` — the fail-safe path.
 */
export function isVersionAtLeast(version: unknown, min: string): boolean {
    const a = parseVersion(version);
    const b = parseVersion(min);
    if (!a || !b) return false;
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
        const x = a[i] ?? 0;
        const y = b[i] ?? 0;
        if (x > y) return true;
        if (x < y) return false;
    }
    return true; // equal
}

/** The version field an engine reports in its presence payload. */
export interface PresenceVersioned {
    app?: unknown;
}

/** Extract the engine version string from a parsed presence payload, if present. */
export function presenceVersion(payload: PresenceVersioned | null | undefined): string | undefined {
    const app = payload?.app;
    return typeof app === 'string' ? app : undefined;
}
